<template>
	<Teleport to="body">
		<div class="modal" :class="{ 'is-active': show }">
			<div class="modal-background" @click="close"></div>
			<div class="modal-card">
				<header class="modal-card-head">
					<p class="modal-card-title">{{ editingItem ? 'Edit item' : 'Add new item' }}</p>
					<button class="delete" aria-label="close" @click="close"></button>
				</header>
				<section class="modal-card-body">
					<div class="field">
						<label class="label">Images</label>
						<image-manager v-model:existing-src="existingImages" v-model:new-images="newImages"></image-manager>
					</div>
					<div class="field">
						<label class="label">Title</label>
						<div class="control">
							<input class="input" type="text" v-model="title" @keyup.enter="submit" />
						</div>
					</div>
					<div class="field">
						<label class="label">Publisher</label>
						<div class="control">
							<input class="input" type="text" v-model="publisher" @keyup.enter="submit" />
						</div>
					</div>
					<div class="field">
						<label class="label">Country</label>
						<div class="control">
							<div class="select is-fullwidth">
								<select v-model="country">
									<option v-for="c in countries" :key="c.name" :value="c.name">{{ c.name }}</option>
								</select>
							</div>
						</div>
					</div>
					<div class="field">
						<label class="label">EAN</label>
						<div class="control">
							<input class="input" type="text" v-model="ean" @keyup.enter="submit" />
						</div>
					</div>
					<div class="field">
						<label class="label">BigBox</label>
						<div class="control">
							<div class="select is-fullwidth">
								<select v-model="bigbox">
									<option>Yes</option>
									<option>No</option>
								</select>
							</div>
						</div>
					</div>
					<div class="field">
						<label class="label">Rarity</label>
						<div class="control">
							<div class="select is-fullwidth">
								<select v-model="rarity">
									<option>Common</option>
									<option>Rare</option>
									<option>Very Rare</option>
									<option>Gem</option>
								</select>
							</div>
						</div>
					</div>
					<div class="field">
						<label class="label">Platform</label>
						<div class="control">
							<div class="select is-fullwidth">
								<select v-model="support">
									<option>PC</option>
									<option>DS</option>
									<option>PS4</option>
									<option>Wii</option>
									<option>Xbox</option>
								</select>
							</div>
						</div>
					</div>
					<div class="field">
						<label class="label">Owned</label>
						<div class="control">
							<div class="select is-fullwidth">
								<select v-model="own">
									<option value="true">Yes</option>
									<option value="soon">On the way</option>
									<option value="false">No</option>
								</select>
							</div>
						</div>
					</div>
					<p class="help is-danger" v-if="error">{{ error }}</p>
					<p class="help is-danger" v-if="submitError">{{ submitError }}</p>
				</section>
				<footer class="modal-card-foot">
					<button class="button is-success" :class="{ 'is-loading': isSubmitting }" :disabled="isSubmitting" @click="submit">
						{{ editingItem ? 'Save' : 'Add' }}
					</button>
					<button class="button" @click="close">Cancel</button>
				</footer>
			</div>
		</div>
	</Teleport>
</template>

<script>
import COUNTRIES from '@/countries.js';
import ImageManager from '@/components/ImageManager.vue';

export default {
	name: 'AddGameModal',
	components: { ImageManager },
	props: {
		show: Boolean,
		editingItem: { type: Object, default: null },
		onSubmit: { type: Function, required: true },
	},
	emits: ['close'],
	data() {
		return {
			countries: COUNTRIES,
			existingImages: [],
			newImages: [],
			title: '',
			publisher: '',
			country: COUNTRIES[0].name,
			ean: '',
			bigbox: 'No',
			rarity: 'Common',
			support: 'PC',
			own: 'true',
			error: '',
			isSubmitting: false,
			submitError: '',
		};
	},
	watch: {
		show(val) {
			if (val) this.populate();
		},
	},
	methods: {
		populate() {
			this.reset();
			if (this.editingItem) {
				const it = this.editingItem;
				this.existingImages = it.image.src.slice();
				this.title = it.title || '';
				this.publisher = it.publisher || '';
				this.country = it.country || COUNTRIES[0].name;
				this.ean = it.ean || '';
				this.bigbox = it.bigbox || 'No';
				this.rarity = it.rarity || 'Common';
				this.support = it.support || 'PC';
				this.own = it.own === 'soon' ? 'soon' : it.own === false ? 'false' : 'true';
			}
		},
		async submit() {
			if (!this.title.trim()) {
				this.error = 'Please enter a title.';
				return;
			}
			if (this.existingImages.length === 0 && this.newImages.length === 0) {
				this.error = 'Please add at least one image.';
				return;
			}
			this.error = '';
			this.submitError = '';
			const payload = {
				existingImages: this.existingImages,
				newImages: this.newImages.map((img) => img.data),
				title: this.title.trim(),
				publisher: this.publisher.trim(),
				country: this.country,
				ean: this.ean.trim(),
				bigbox: this.bigbox,
				rarity: this.rarity,
				support: this.support,
				own: this.own === 'soon' ? 'soon' : this.own === 'true',
			};
			this.isSubmitting = true;
			const result = await this.onSubmit({
				isEdit: !!this.editingItem,
				id: this.editingItem ? this.editingItem.id : null,
				payload,
			});
			this.isSubmitting = false;
			if (result && result.ok) {
				this.reset();
				this.$emit('close');
			} else {
				this.submitError = (result && result.error) || 'Could not save this item.';
			}
		},
		close() {
			this.reset();
			this.$emit('close');
		},
		reset() {
			this.existingImages = [];
			this.newImages = [];
			this.title = '';
			this.publisher = '';
			this.country = COUNTRIES[0].name;
			this.ean = '';
			this.bigbox = 'No';
			this.rarity = 'Common';
			this.support = 'PC';
			this.own = 'true';
			this.error = '';
			this.isSubmitting = false;
			this.submitError = '';
		},
	},
};
</script>

<style scoped>
.modal {
	z-index: 1000;
}
.modal-card {
	width: 480px;
	max-width: 90vw;
}
.modal-card-body {
	scrollbar-width: thin;
}
</style>
